(() => {
  const FORM_SELECTOR = [
    '.calinfi-distributor__form',
    '.calinfi-contact__form',
    '.calinfi-header__explore-form',
    '.calinfi-footer__newsletter-form',
    '.newsletter-form',
    '.calinfi-email-popup__form',
    '.calinfi-auth__form',
    '#ContactForm',
    '.customer.activate form'
  ].join(',');

  const FIELD_SELECTOR = [
    '.calinfi-distributor__field',
    '.calinfi-distributor__consent',
    '.calinfi-contact__field',
    '.calinfi-contact__consent',
    '.calinfi-footer__field',
    '.calinfi-auth__field',
    '.calinfi-auth__check',
    '.field'
  ].join(',');

  const getField = (control) => control.closest(FIELD_SELECTOR) || control.parentElement;

  const getLabel = (control) => {
    const label = control.labels?.[0]?.textContent || control.getAttribute('aria-label') || 'This field';
    return label.replace(/\s*\*\s*$/, '').trim();
  };

  const getMessage = (control) => {
    if (control.validity.valueMissing) {
      if (control.type === 'checkbox') return 'Please confirm this field.';
      if (control.tagName === 'SELECT') return 'Select an option.';
      return 'Complete this required field.';
    }

    if (control.validity.typeMismatch && control.type === 'email') return 'Enter a valid email address.';
    if (control.validity.typeMismatch && control.type === 'url') return 'Enter a complete website address, including https://.';
    if (control.validity.patternMismatch && control.type === 'url') return 'Enter a complete website address, including https://.';
    if (control.validity.tooShort) return `Enter at least ${control.minLength} characters.`;
    return control.validationMessage || `Check ${getLabel(control).toLowerCase()}.`;
  };

  const ensureSummary = (form) => {
    let summary = form.querySelector('[data-calinfi-form-summary]');
    if (summary) return summary;

    summary = document.createElement('div');
    summary.className = 'calinfi-form__summary';
    summary.dataset.calinfiFormSummary = '';
    summary.setAttribute('role', 'alert');
    summary.setAttribute('tabindex', '-1');
    summary.hidden = true;

    const firstVisibleChild = Array.from(form.children).find((child) => child.type !== 'hidden');
    form.insertBefore(summary, firstVisibleChild || form.firstChild);
    return summary;
  };

  const clearFieldError = (control) => {
    const field = getField(control);
    control.removeAttribute('aria-invalid');
    field?.classList.remove('calinfi-form__field--invalid');

    const errorId = control.dataset.calinfiErrorId;
    if (!errorId) return;

    document.getElementById(errorId)?.remove();
    const describedBy = (control.getAttribute('aria-describedby') || '')
      .split(/\s+/)
      .filter((id) => id && id !== errorId)
      .join(' ');

    if (describedBy) control.setAttribute('aria-describedby', describedBy);
    else control.removeAttribute('aria-describedby');
    delete control.dataset.calinfiErrorId;
  };

  const showFieldError = (control, index) => {
    const field = getField(control);
    if (!field) return;

    clearFieldError(control);
    if (control.matches('[data-channel-checkbox]')) {
      field.classList.add('calinfi-form__field--invalid');
      control.setAttribute('aria-invalid', 'true');
      return;
    }

    const error = document.createElement('p');
    const errorId = `CalinfiFormError-${control.form?.id || 'form'}-${control.id || control.name || index}`
      .replace(/[^a-zA-Z0-9_-]/g, '-');

    error.id = errorId;
    error.className = 'calinfi-form__field-error';
    error.textContent = getMessage(control);
    field.appendChild(error);

    field.classList.add('calinfi-form__field--invalid');
    control.setAttribute('aria-invalid', 'true');
    const describedBy = new Set((control.getAttribute('aria-describedby') || '').split(/\s+/).filter(Boolean));
    describedBy.add(errorId);
    control.setAttribute('aria-describedby', Array.from(describedBy).join(' '));
    control.dataset.calinfiErrorId = errorId;
  };

  const syncChannelValidity = (form) => {
    const channels = Array.from(form.querySelectorAll('[data-channel-checkbox]'));
    if (!channels.length) return;

    const hasChannel = channels.some((control) => control.checked);
    channels.forEach((control, index) => {
      control.setCustomValidity(!hasChannel && index === 0 ? 'Choose at least one sales channel.' : '');
    });

    const existingError = form.querySelector('[data-channel-error]');
    if (existingError) existingError.hidden = hasChannel;
    if (hasChannel) clearFieldError(channels[0]);
  };

  const syncPasswordValidity = (form) => {
    const password = form.querySelector('input[name="customer[password]"]');
    const confirmation = form.querySelector('input[name="customer[password_confirmation]"]');
    if (!password || !confirmation) return;

    const passwordsDiffer = Boolean(confirmation.value) && password.value !== confirmation.value;
    confirmation.setCustomValidity(passwordsDiffer ? 'Passwords do not match.' : '');
    if (!passwordsDiffer) clearFieldError(confirmation);
  };

  const updateSummary = (summary, count) => {
    if (!count) {
      summary.hidden = true;
      summary.textContent = '';
      return;
    }

    summary.textContent = count === 1
      ? 'Please correct the highlighted field.'
      : `Please correct the ${count} highlighted fields.`;
    summary.hidden = false;
  };

  const getInvalidControls = (form) => {
    syncChannelValidity(form);
    syncPasswordValidity(form);
    return Array.from(form.elements).filter((control) => control.willValidate && !control.validity.valid);
  };

  const resetSubmittingState = (form) => {
    form.classList.remove('is-submitting');
    const submit = form.querySelector('[type="submit"]');
    submit?.removeAttribute('aria-busy');
  };

  const initializeForm = (form) => {
    if (form.dataset.calinfiValidationReady === 'true') return;
    form.dataset.calinfiValidationReady = 'true';

    // Keep native constraint validation enabled. This prevents Shopify's
    // hCaptcha layer from running when a form is already invalid.
    form.noValidate = false;
    const summary = ensureSummary(form);
    let validationRenderScheduled = false;

    const renderValidationErrors = () => {
      Array.from(form.querySelectorAll('[data-calinfi-error-id]')).forEach(clearFieldError);
      const invalidControls = getInvalidControls(form);

      if (!invalidControls.length) {
        updateSummary(summary, 0);
        return false;
      }

      resetSubmittingState(form);
      invalidControls.forEach(showFieldError);
      updateSummary(summary, invalidControls.length);
      summary.focus({ preventScroll: true });
      summary.scrollIntoView({ behavior: 'smooth', block: 'center' });
      invalidControls[0].focus({ preventScroll: true });
      return true;
    };

    const scheduleValidationErrors = () => {
      if (validationRenderScheduled) return;
      validationRenderScheduled = true;
      queueMicrotask(() => {
        validationRenderScheduled = false;
        renderValidationErrors();
      });
    };

    syncChannelValidity(form);
    syncPasswordValidity(form);

    const revalidateControl = (event) => {
      const control = event.target;
      if (!(control instanceof HTMLInputElement || control instanceof HTMLSelectElement || control instanceof HTMLTextAreaElement)) return;
      syncChannelValidity(form);
      syncPasswordValidity(form);
      if (control.validity.valid) clearFieldError(control);
      if (!summary.hidden) updateSummary(summary, getInvalidControls(form).length);
    };

    form.addEventListener('input', revalidateControl);
    form.addEventListener('change', revalidateControl);

    form.addEventListener('invalid', (event) => {
      event.preventDefault();
      scheduleValidationErrors();
    }, true);

    form.addEventListener('click', (event) => {
      const submit = event.target.closest?.('[type="submit"], button:not([type])');
      if (!submit || !form.contains(submit)) return;
      if (!getInvalidControls(form).length) return;

      event.preventDefault();
      event.stopPropagation();
      renderValidationErrors();
    }, true);

    form.addEventListener('submit', (event) => {
      if (renderValidationErrors()) {
        event.preventDefault();
        event.stopImmediatePropagation();
        resetSubmittingState(form);
        return;
      }

      form.classList.add('is-submitting');
      form.querySelector('[type="submit"]')?.setAttribute('aria-busy', 'true');
    }, true);
  };

  const initializeForms = (root = document) => {
    if (root.matches?.(FORM_SELECTOR)) initializeForm(root);
    root.querySelectorAll?.(FORM_SELECTOR).forEach(initializeForm);
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initializeForms());
  } else {
    initializeForms();
  }

  document.addEventListener('shopify:section:load', (event) => initializeForms(event.target));
})();
